/**
 * Planets, house-occupancy and angles tables of the chart view.
 * Moved unchanged from screens/me/NatalChart.tsx.
 */

import { useTranslation } from 'react-i18next';
import type { NatalSelectKind } from './NatalChartSvg';
import type { ChartPlanet, ChartHouse, ChartAngles } from '../../hooks/useChart';
import styles from '../../screens/me/MeSubpages.module.css';
import { PLANET_GLYPH, ANGLE_LABEL, fmtDeg, planetLabel, signLabel, signShort } from './chartViewHelpers';

export function PlanetsTable({ planets, onPick }: {
  planets: ChartPlanet[];
  onPick: (kind: NatalSelectKind, id: string) => void;
}): JSX.Element {
  const { t } = useTranslation('me');
  return (
    <section className={styles.tableCard} aria-labelledby="planets-h">
      <h2 id="planets-h" className={styles.tableHeader}>{t('natalChart.chrome.planetsHeader')}</h2>
      <table className={styles.table} data-testid="natal-planets-table">
        <thead>
          <tr><th>{t('natalChart.chrome.colPlanet')}</th><th>{t('natalChart.chrome.colSign')}</th><th>{t('natalChart.chrome.colHouse')}</th><th style={{ textAlign: 'right' }}>°</th></tr>
        </thead>
        <tbody>
          {planets.map((p) => (
            <tr key={p.id}>
              <td>
                <button type="button" className={styles.linkCell} onClick={() => onPick('planet', p.id)}>
                  <span className={styles.tableGlyph} aria-hidden="true">{PLANET_GLYPH[p.id] ?? '·'}</span>
                  {planetLabel(p.id, t)}
                  {p.retrograde && <span className={styles.retroFlag} aria-label="retrograde">R</span>}
                </button>
              </td>
              <td className={styles.tableSign}>
                <button type="button" className={styles.linkCell} onClick={() => onPick('sign', p.sign)}>
                  {signLabel(p.sign, t)}
                </button>
              </td>
              <td>
                {p.house ? (
                  <button type="button" className={styles.linkCell} onClick={() => onPick('house', String(p.house))}>
                    {p.house}
                  </button>
                ) : '—'}
              </td>
              <td className={styles.tableNum}>{fmtDeg(p.position)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function HouseOccupancyTable({ houses, planets, onPick }: {
  houses: ChartHouse[];
  planets: ChartPlanet[];
  onPick: (kind: NatalSelectKind, id: string) => void;
}): JSX.Element {
  const { t } = useTranslation('me');
  // Bucket planets by their house number.
  const byHouse = new Map<number, ChartPlanet[]>();
  for (const p of planets) {
    if (!p.house) continue;
    const arr = byHouse.get(p.house) ?? [];
    arr.push(p);
    byHouse.set(p.house, arr);
  }

  const houseInfo = new Map<number, { sign: string; position: number }>(
    houses.map((h) => [h.house, { sign: h.sign, position: h.position }]),
  );

  const rows = Array.from({ length: 12 }, (_, i) => i + 1).map((houseNum) => {
    const info = houseInfo.get(houseNum);
    return {
      house: houseNum,
      sign: info?.sign ?? null,
      position: info?.position ?? null,
      occupants: byHouse.get(houseNum) ?? [],
    };
  });

  return (
    <section className={styles.tableCard} aria-labelledby="occupancy-h">
      <h2 id="occupancy-h" className={styles.tableHeader}>{t('natalChart.chrome.housesHeader')}</h2>
      <table className={styles.table} data-testid="natal-house-occupancy-table">
        <thead>
          <tr><th>{t('natalChart.chrome.colHouse')}</th><th>{t('natalChart.chrome.colCusp')}</th><th>{t('natalChart.chrome.planetsHeader')}</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.house}>
              <td>
                <button type="button" className={styles.linkCell} onClick={() => onPick('house', String(r.house))}>
                  <span className={styles.tableGlyph} aria-hidden="true">{r.house}</span>
                </button>
              </td>
              <td className={styles.tableSign}>
                {r.sign ? (
                  <button type="button" className={styles.linkCell} onClick={() => onPick('sign', r.sign as string)}>
                    {signLabel(r.sign as string, t)}
                    {r.position !== null && (
                      <span className={styles.cuspDeg}>{fmtDeg(r.position)}</span>
                    )}
                  </button>
                ) : <span className={styles.tableEmpty}>—</span>}
              </td>
              <td>
                {r.occupants.length === 0 ? (
                  <span className={styles.tableEmpty}>—</span>
                ) : (
                  <span className={styles.occupants}>
                    {r.occupants.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={styles.occupantChip}
                        onClick={() => onPick('planet', p.id)}
                        title={`${planetLabel(p.id, t)} · ${signLabel(p.sign, t)}`}
                      >
                        <span aria-hidden="true">{PLANET_GLYPH[p.id] ?? '·'}</span>
                        <span className={styles.occupantSign}>{signShort(p.sign, t)}</span>
                        {p.retrograde && <span className={styles.retroFlag} aria-hidden="true">R</span>}
                      </button>
                    ))}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function AnglesTable({ angles, onPick }: {
  angles: ChartAngles;
  onPick: (kind: NatalSelectKind, id: string) => void;
}): JSX.Element {
  const { t } = useTranslation('me');
  const rows: Array<{ id: 'asc' | 'mc' | 'ic' | 'dc'; sign: string; pos: number }> = [
    { id: 'asc', sign: angles.asc.sign, pos: angles.asc.position },
    { id: 'mc', sign: angles.mc.sign, pos: angles.mc.position },
    { id: 'ic', sign: angles.ic.sign, pos: angles.ic.position },
    { id: 'dc', sign: angles.dc.sign, pos: angles.dc.position },
  ];
  return (
    <section className={styles.tableCard} aria-labelledby="angles-h">
      <h2 id="angles-h" className={styles.tableHeader}>{t('natalChart.chrome.anglesHeader')}</h2>
      <table className={styles.table} data-testid="natal-angles-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <button type="button" className={styles.linkCell} onClick={() => onPick('angle', r.id)}>
                  <span className={styles.tableGlyph} aria-hidden="true">{ANGLE_LABEL[r.id]}</span>
                </button>
              </td>
              <td className={styles.tableSign}>
                <button type="button" className={styles.linkCell} onClick={() => onPick('sign', r.sign)}>
                  {signLabel(r.sign, t)}
                </button>
              </td>
              <td className={styles.tableNum}>{fmtDeg(r.pos)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
